import { Typography, Skeleton } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconBriefcase } from "@tabler/icons-react";
import dayjs from "dayjs";
import { DataTable } from "@/components/tables";
import type { TableHeaderType } from "@/components/tables/types";
import ColorTag from "@/components/tags/ColorTag";
import {
  EndpointCallersResponse,
  AIFunctionUsageBreakdownResponse,
  EndpointCallerItem,
  AIFunctionUsageItem,
} from "../types";
import { formatNumber } from "@/uiCore/components/formatters/format";
import {
  getUsageTypeConfig,
  getCallerTypeConfig,
  getCallerIdentity,
  getAIFunctionIdentity,
  getUsageTypeColor,
  getCallerTypeColor,
} from "./endpointDetailUtils";

interface EndpointUsageTableProps {
  callersData: EndpointCallersResponse | undefined;
  usageBreakdownData: AIFunctionUsageBreakdownResponse | undefined;
  isAIFunction: boolean;
  isLoading: boolean;
}

const aiUsageColumns: TableHeaderType<AIFunctionUsageItem>[] = [
  {
    id: 1,
    uid: "usage_type",
    label: "Type",
    width: "140px",
    render: (item: AIFunctionUsageItem) => {
      const typeConfig = getUsageTypeConfig(item.usage_type);
      return (
        <Stack direction="row" align="center" gap="xs">
          <typeConfig.icon className={`al-h-4 al-w-4 ${typeConfig.color}`} />
          <ColorTag color={getUsageTypeColor(item.usage_type)} label={item.usage_type} />
        </Stack>
      );
    },
  },
  {
    id: 2,
    uid: "usage_identity",
    label: "Identity",
    render: (item: AIFunctionUsageItem) => {
      const identity = getAIFunctionIdentity(item);
      return (
        <Stack direction="column" gap="xxs">
          <Typography size="sm" weight="medium" className="al-text-gray-900">
            {identity.name}
          </Typography>
          {identity.subtitle && (
            <Typography size="xs" className="al-font-mono al-text-gray-500">
              {identity.subtitle}
            </Typography>
          )}
        </Stack>
      );
    },
  },
  {
    id: 3,
    uid: "total_requests",
    label: "Invocations",
    width: "120px",
    render: (item: AIFunctionUsageItem) => (
      <Typography weight="medium" size="sm">
        {formatNumber(item.total_requests)}
      </Typography>
    ),
  },
  {
    id: 4,
    uid: "days_active",
    label: "Days Active",
    width: "100px",
    render: (item: AIFunctionUsageItem) => <Typography size="sm">{item.days_active}</Typography>,
  },
  {
    id: 5,
    uid: "avg_per_day",
    label: "Avg/Day",
    width: "100px",
    render: (item: AIFunctionUsageItem) => (
      <Typography size="sm">
        {item.days_active > 0 ? (item.total_requests / item.days_active).toFixed(1) : "0"}
      </Typography>
    ),
  },
  {
    id: 6,
    uid: "last_used",
    label: "Last Used",
    width: "180px",
    render: (item: AIFunctionUsageItem) => (
      <Typography size="sm" className="al-text-gray-500">
        {item.last_used ? dayjs(item.last_used).format("MMM DD, YYYY HH:mm") : "\u2014"}
      </Typography>
    ),
  },
];

const callerColumns: TableHeaderType<EndpointCallerItem>[] = [
  {
    id: 1,
    uid: "caller_type",
    label: "Type",
    width: "140px",
    render: (caller: EndpointCallerItem) => {
      const typeConfig = getCallerTypeConfig(caller.caller_type);
      return (
        <Stack direction="row" align="center" gap="xs">
          <typeConfig.icon className={`al-h-4 al-w-4 ${typeConfig.color}`} />
          <ColorTag color={getCallerTypeColor(caller.caller_type)} label={typeConfig.label} />
        </Stack>
      );
    },
  },
  {
    id: 2,
    uid: "identity",
    label: "Identity",
    render: (caller: EndpointCallerItem) => {
      const identity = getCallerIdentity(caller);
      return (
        <Stack direction="column" gap="xxs">
          <Typography size="sm" weight="medium" className="al-text-gray-900">
            {identity.name}
          </Typography>
          {identity.subtitle && (
            <Typography size="xs" className="al-font-mono al-text-gray-500">
              {identity.subtitle}
            </Typography>
          )}
        </Stack>
      );
    },
  },
  {
    id: 3,
    uid: "query_count",
    label: "Invocations",
    width: "120px",
    render: (caller: EndpointCallerItem) => (
      <Typography weight="medium" size="sm">
        {formatNumber(caller.query_count)}
      </Typography>
    ),
  },
  {
    id: 4,
    uid: "days_active",
    label: "Days Active",
    width: "100px",
    render: (caller: EndpointCallerItem) => <Typography size="sm">{caller.days_active}</Typography>,
  },
  {
    id: 5,
    uid: "avg_queries_per_day",
    label: "Avg/Day",
    width: "100px",
    render: (caller: EndpointCallerItem) => (
      <Typography size="sm">{caller.avg_queries_per_day.toFixed(1)}</Typography>
    ),
  },
  {
    id: 6,
    uid: "last_execution",
    label: "Last Used",
    width: "180px",
    render: (caller: EndpointCallerItem) => (
      <Typography size="sm" className="al-text-gray-500">
        {caller.last_execution
          ? dayjs(caller.last_execution).format("MMM DD, YYYY HH:mm")
          : "\u2014"}
      </Typography>
    ),
  },
];

const EndpointUsageTable = ({
  callersData,
  usageBreakdownData,
  isAIFunction,
  isLoading,
}: EndpointUsageTableProps): JSX.Element => {
  const hasAIUsageData = isAIFunction && usageBreakdownData && usageBreakdownData.usage.length > 0;
  const hasCallersData = callersData && callersData.callers.length > 0;
  const showAIUsageView = isAIFunction && hasAIUsageData;

  const renderBody = (): JSX.Element => {
    if (isLoading) {
      return (
        <Stack direction="column" gap="sm" className="al-p-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="al-h-12 al-w-full" />
          ))}
        </Stack>
      );
    }
    if (showAIUsageView) {
      return (
        <DataTable
          header={aiUsageColumns}
          data={{ items: usageBreakdownData?.usage ?? [] }}
          isLoading={false}
          disableDynamicHeight
        />
      );
    }
    if (hasCallersData) {
      return (
        <DataTable
          header={callerColumns}
          data={{ items: callersData?.callers ?? [] }}
          isLoading={false}
          disableDynamicHeight
        />
      );
    }
    return (
      <Stack direction="column" align="center" justify="center" className="al-py-8">
        <IconBriefcase className="al-h-12 al-w-12 al-text-gray-300 al-mb-2" />
        <Typography className="al-text-gray-500">
          No usage data found for this endpoint in the selected time period.
        </Typography>
      </Stack>
    );
  };

  const showingCaption =
    hasCallersData && callersData.pagination?.total > 0 && !showAIUsageView ? (
      <Typography size="sm" className="al-text-gray-500">
        Showing {callersData.callers.length} of {callersData.pagination.total} sources
      </Typography>
    ) : null;

  return (
    <Stack direction="column" gap="sm">
      {showingCaption && (
        <Stack direction="row" justify="end" className="al-w-full">
          {showingCaption}
        </Stack>
      )}
      {renderBody()}
    </Stack>
  );
};

export default EndpointUsageTable;
